import { Account } from '@/db/schema/account'
import { AccountTypeValue, BookingTypeValue } from '@/db/types'
import { ResumesType } from '@/types/resume'
import { json, jsonb, pgEnum, pgTable, serial, text, timestamp } from 'drizzle-orm/pg-core'
import { z } from 'zod'

export const userGender = pgEnum('user_gender', ['men', 'women', 'other'])
export const userRoles = pgEnum('user_roles', ['member', 'accountant', 'admin', 'aspirant'])

export default jsonb

export const UserProfileAttributesSchema = {
  address: z.string().optional(),
  telegram: z.string().optional(),
  linkedin: z.string().optional(),
  webpage: z.string().optional(),
  github: z.string().optional(),
  openForWork: z.string().optional(),
  iban: z.string().optional(),
  birthday: z
    .transform((_) => {
      return new Date(`${_}`)
    })
    .optional(),
  gender: z.enum(userGender.enumValues).optional(),
  mobile: z.string().optional(),
  ahv: z.string().optional(),
}
export const UserSalaryAttributesSchema = {
  year: z.string(),
  hasGA: z.boolean(),
  hasVerpflegung: z.boolean(),
  kinderzulagen: z.string(),
  ktg: z.string(),
  bvgAn: z.string(),
  bvgAg: z.string(),
  bvgCorrection: z.string().optional().nullable(),
  workload: z.string(),
  salaries: z.string().array(),
  lohnausweis: z.string().optional(),
}
export const UserContractAttributesSchema = {
  ends_at: z.string().optional(),
  description: z.string(),
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const userProfileAttributesSchema = z.object(UserProfileAttributesSchema)
export type UserProfileAttributesType = z.infer<typeof userProfileAttributesSchema>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const userSalaryAttributesSchema = z.object(UserSalaryAttributesSchema)
export type UserSalaryAttributesType = z.infer<typeof userSalaryAttributesSchema>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const userContractAttributesSchema = z.object(UserContractAttributesSchema)
export type UserContractAttributesType = z.infer<typeof userContractAttributesSchema>

/**
 * The `sub` of the non-human user that owns automated bookings (salary runs, Revolut ingest). It is
 * never bound to a Google account — see `syncGoogleUser`.
 */
export const SYSTEM_USER_SUB = 'system'

export const user = pgTable('user', {
  id: serial('id').primaryKey(), // Auto-incrementing ID
  migrationId: text('migration_id'), // Migration ID
  created: timestamp().defaultNow().notNull(),
  updated: timestamp().defaultNow().notNull(),
  sub: text().notNull().unique(),
  iss: text().notNull(),
  firstname: text().notNull(),
  lastname: text().notNull(),
  email: text().notNull().unique(),
  picture: text(),
  roles: userRoles().array().default([]).notNull(),
  entered: timestamp().defaultNow().notNull(),
  exited: timestamp(),
  profile: jsonb().$type<UserProfileAttributesType>().notNull(),
  salary: jsonb().$type<{ configurations: UserSalaryAttributesType[] }>().notNull(),
  contract: jsonb()
    .$type<UserContractAttributesType>()
    .notNull()
    .default({} as UserContractAttributesType),
  resumes: json().$type<ResumesType>(),
})

export const safeJsonb = (u: User) => {
  // make sure that the jsonb values are parsed into proper types
  // date is safed as string in json, but we need a date object
  // this type is not checked on jsonb level in postgres, so we need to do it here
  if (u?.profile.birthday) {
    u.profile.birthday = new Date(u.profile.birthday)
  }
  return u
}

export type Balancesheet = {
  type: BookingTypeValue
  accountType: AccountTypeValue
  total: number
}
export type User = typeof user.$inferSelect & { accounts?: Account[]; balancesheets?: Balancesheet[] }
export type UserBasic = Pick<User, 'id' | 'firstname' | 'lastname' | 'email' | 'picture'>
export type InsertUser = typeof user.$inferInsert
export type UpdateUser = Partial<User>
